import React from 'react';
import { ArrowDown, ArrowUp, GripVertical, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export const SortableTodayPlanGoalCard = ({
  goalId,
  index,
  totalGoals,
  priority,
  lang,
  isBlocking,
  hasTimeRange,
  warningState,
  onChangeField,
  onRegisterEndNormalization,
  onBlock,
  onClearTime,
  onRemove,
  onMoveUp,
  onMoveDown,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: goalId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl border bg-stone-50 p-3 transition-all ${isDragging ? 'border-primary ring-2 ring-primary/20 shadow-lg z-10' : 'border-stone-200'}`}
      data-testid={`planner-today-plan-priority-card-${index + 1}`}
    >
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          {totalGoals > 1 && (
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white border border-stone-200 text-stone-400 cursor-grab active:cursor-grabbing touch-none"
              data-testid={`planner-today-plan-drag-handle-${index + 1}`}
              title={lang === 'hr' ? 'Prevuci za promjenu redoslijeda' : 'Drag to reorder'}
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
          <p className="text-xs uppercase tracking-wide text-[#78716C]">
            {lang === 'hr' ? `Cilj ${index + 1}` : `Goal ${index + 1}`}
          </p>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {totalGoals > 1 && (
            <>
              <button
                type="button"
                onClick={onMoveUp}
                disabled={index === 0}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-stone-200 bg-white text-stone-500 hover:bg-stone-50 disabled:opacity-40"
                data-testid={`planner-today-plan-move-up-button-${index + 1}`}
                title={lang === 'hr' ? 'Pomjeri gore' : 'Move up'}
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={index === totalGoals - 1}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-stone-200 bg-white text-stone-500 hover:bg-stone-50 disabled:opacity-40"
                data-testid={`planner-today-plan-move-down-button-${index + 1}`}
                title={lang === 'hr' ? 'Pomjeri dolje' : 'Move down'}
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
            </>
          )}

          {totalGoals > 1 && (
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
              data-testid={`planner-today-plan-remove-goal-button-${index + 1}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {lang === 'hr' ? 'Ukloni' : 'Remove'}
            </button>
          )}
        </div>
      </div>

      <input
        type="text"
        value={priority?.title || ''}
        onChange={(e) => onChangeField('title', e.target.value)}
        className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm text-[#292524]"
        placeholder={lang === 'hr' ? 'Naziv cilja' : 'Goal title'}
        data-testid={`planner-today-plan-priority-title-input-${index + 1}`}
      />

      <textarea
        value={priority?.goal_niyyah || ''}
        onChange={(e) => onChangeField('goal_niyyah', e.target.value)}
        className="w-full mt-2 px-3 py-2 border border-stone-200 rounded-lg text-sm text-[#292524] resize-none h-16"
        placeholder={lang === 'hr' ? 'Nijet za ovaj cilj (opcionalno)' : 'Niyyah for this goal (optional)'}
        data-testid={`planner-today-plan-priority-niyyah-input-${index + 1}`}
      />

      <div className="mt-2 grid grid-cols-[1fr_auto_1fr_auto] items-end gap-2">
        <div>
          <label className="block text-xs text-[#78716C] mb-1 font-medium">
            {lang === 'hr' ? 'Od' : 'From'}
          </label>
          <input
            type="time"
            value={priority?.start_time || ''}
            onChange={(e) => onChangeField('start_time', e.target.value)}
            className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm text-[#292524]"
            data-testid={`planner-today-plan-priority-start-input-${index + 1}`}
          />
        </div>

        <span className="text-stone-400 text-sm pb-2">–</span>

        <div>
          <label className="block text-xs text-[#78716C] mb-1 font-medium">
            {lang === 'hr' ? 'Do' : 'To'}
          </label>
          <input
            type="time"
            value={priority?.end_time || ''}
            onChange={(e) => {
              onChangeField('end_time', e.target.value);
              onRegisterEndNormalization();
            }}
            className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm text-[#292524]"
            data-testid={`planner-today-plan-priority-end-input-${index + 1}`}
          />
        </div>

        {hasTimeRange ? (
          <button
            type="button"
            onClick={onBlock}
            disabled={isBlocking}
            className="px-3 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 disabled:opacity-50 whitespace-nowrap"
            data-testid={`planner-today-plan-priority-block-button-${index + 1}`}
          >
            {isBlocking ? (lang === 'hr' ? '...' : '...') : (lang === 'hr' ? 'Blokiraj' : 'Block')}
          </button>
        ) : (
          <div className="px-3 py-2 text-xs text-stone-400 whitespace-nowrap">
            {lang === 'hr' ? 'Bez termina' : 'No time'}
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onClearTime}
          className="text-xs text-stone-500 hover:text-stone-700"
          data-testid={`planner-today-plan-priority-clear-time-button-${index + 1}`}
        >
          {lang === 'hr' ? 'Ukloni vrijeme' : 'Clear time'}
        </button>
      </div>

      {priority?.blocked_event_id && (
        <p className="text-xs text-green-700 mt-2" data-testid={`planner-today-plan-priority-blocked-text-${index + 1}`}>
          ✓ {lang === 'hr' ? 'Blokirano u kalendaru' : 'Blocked in calendar'}
        </p>
      )}

      {warningState?.invalidRange && (
        <p className="text-xs text-amber-800 mt-2" data-testid={`planner-today-plan-priority-warning-${index + 1}`}>
          {lang === 'hr' ? 'Ako uneseš vrijeme, postavi oba polja i završetak mora biti nakon početka.' : 'If you set a time, fill both fields and keep the end after the start.'}
        </p>
      )}

      {!warningState?.invalidRange && warningState?.sequenceConflict && (
        <p className="text-xs text-amber-800 mt-2" data-testid={`planner-today-plan-priority-warning-${index + 1}`}>
          {lang === 'hr' ? 'Ovaj cilj se preklapa ili remeti redoslijed drugih ciljeva. Možeš ipak potvrditi pri spremanju.' : 'This goal overlaps or breaks the order of other goals. You can still confirm it when saving.'}
        </p>
      )}
    </div>
  );
};
